import { Helmet } from "react-helmet-async";
import React, { useEffect, useState } from "react";
import { fetchSpaceData } from "services/spacePage.services";
import {
  SpaceLocationMap,
  ReviewStars,
  PricesTable,
  ServiceSvgIcon,
  Tooltip,
} from "reusableComponents";
import { useTranslation } from "react-i18next";
import NotFoundPage from "views/NotFoundPage";
import LoadingPage from "views/LoadingPage";
import { useNavigate, useParams } from "react-router";
import { useLanguage } from "context";
import { isFavorite } from "store/favorites/selectors";
import { getQueryParamsFromFilters } from "helpers/searchParams";
import { currencyToSymbol } from "helpers/currencyToSymbol";
import LandingPageSlider from "views/LandingPage/components/LandingPageSlider";
import { useSelector, useDispatch } from "react-redux";
import { toggleAddToFavorite } from "store/favorites/thunks";
import CompactBookingForm from "reusableComponents/booking/CompactBookingForm";
import {
  heartIconFilled,
  heartIconOutlined,
  ShareIcon,
} from "assets/icons/spacePageIcons";
import { getNearbyPlaces } from "services/spaceList.services";
import { AreaIcon } from "assets/icons/spacePageIcons/svgAsComponentExports";
import { ShareSpaceModal } from "modals";
import { useModal } from "context";
import { RequestQuotationForm } from "modals";
import { ReactComponent as AskAQuestionIcon } from "assets/icons/askAQuestion.svg";
import getFilteredDispositionTypes from "helpers/getSpaceDispositionTypes";
import SimpleConfirmationModal from "modals/SimpleConfirmationModal";
import { getStreetName } from "./helpers";
import { LocationIcon } from "assets/icons/svgComponents";
import SpaceGallery from "./SpaceGallery";
import CTAButton from "modules/Header/components/CTAButton";
import styles from "./styles.module.scss";
import autogenerateSpaceTitle from "helpers/autogenerateSpaceTitle";
import Reviews from "./Reviews";
import SpacePageHelmet from "./SpacePageHelmet";
import { getToken } from "helpers/tokenHelpers";

const SpacePage = ({ forMemberPlus = false, embedded = false }) => {
  const [t] = useTranslation("global");
  const { spacePageId, slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const dispatch = useDispatch();
  const { themeColor = "pink" } = useParams();
  const filters = useSelector((state) => state.filters);
  const { openModal, closeModal } = useModal();

  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const userType = useSelector((state) => state.auth.userType);
  const currentUser = useSelector((state) => state.auth.currentUser);

  const [nearbySpaces, setNearbySpaces] = useState([]);
  const [spaceData, setSpaceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [spaceStatus, setSpaceStatus] = useState(null);
  const [localIsFavorited, setLocalIsFavorited] = useState(false);
  const [isProcessingFavorite, setIsProcessingFavorite] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isBookingInProgress, setIsBookingInProgress] = useState(false);
  
  // ============================================================
  // ADDED: Track OAuth loading state for mobile redirect case
  // ============================================================
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);

  const isSeminarOrTeambuilding = (usageKey) => {
    return usageKey === "seminar" || usageKey === "teambuilding" || usageKey === "conference_room";
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

// ============================================================
// Check for OAuth pending state on mount
// ============================================================
useEffect(() => {
  const hasPendingOAuth = sessionStorage.getItem('has_pending_booking_after_oauth') === 'true';
  const justLoggedIn = sessionStorage.getItem('just_logged_in_for_booking') === 'true';
  const justRegistered = sessionStorage.getItem('just_registered_for_booking') === 'true';
  const hasToken = !!getToken();   // ← CHANGE THIS LINE
  
  if (hasToken && (hasPendingOAuth || justLoggedIn || justRegistered)) {
    // Authenticated + pending booking = legitimate in-progress flow
    console.log('[SpacePage] OAuth pending detected, showing loading overlay');
    setIsOAuthLoading(true);
  } else if (!hasToken && (hasPendingOAuth || justLoggedIn || justRegistered)) {
    // ============================================================
    // FIX: Stale flags from abandoned registration.
    // AuthEndpoint set these optimistically before redirecting to
    // /register, but the user closed the modal without registering.
    // No token = not authenticated = no booking can proceed.
    // Clean up so the overlay doesn't show forever.
    // ============================================================
    console.log('[SpacePage] Stale OAuth flags detected without token - cleaning up');
    sessionStorage.removeItem('has_pending_booking_after_oauth');
    sessionStorage.removeItem('just_logged_in_for_booking');
    sessionStorage.removeItem('just_registered_for_booking');
    sessionStorage.removeItem('pending_booking_data');
    sessionStorage.removeItem('booking_space_id');
    sessionStorage.removeItem('google_registration_data');
    sessionStorage.removeItem('google_oauth_intent');
    sessionStorage.removeItem('google_signup_type');
    sessionStorage.removeItem('google_oauth_state');
  }
}, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const adminPreview = urlParams.get("adminPreview");
    const uid = urlParams.get("uid");
    const spaceIdFromParam = spacePageId;
    if (adminPreview === "1" && uid) {
      console.log("[SpacePage] Admin preview detected, redirecting to login");
      const returnParams = { spaceID: spaceIdFromParam };
      const loginUrl = `/login?rt=app.space&rtp=${encodeURIComponent(JSON.stringify(returnParams))}&uid=${uid}`;
      window.location.href = loginUrl;
    }
  }, [spacePageId]);

  const extractSpaceId = (param) => {
    const match = param?.match(/^(czw-[a-zA-Z0-9]+)/i);
    return match ? match[1] : param;
  };
  const spaceId = extractSpaceId(spacePageId);

  useEffect(() => {
    if (themeColor && themeColor !== "null" && forMemberPlus) {
      document.documentElement.style.setProperty("--main-color", themeColor);
      document.documentElement.style.setProperty("--navColor", themeColor);
    }
  }, [themeColor, forMemberPlus]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchSpaceData(spaceId, language);
        if (data && data.status) {
          setSpaceStatus(data.status);
        }
        setSpaceData(data);
      } catch (err) {
        console.error("[SpacePage] Error fetching space:", err);
        if (err.message?.includes("401") || err.message?.includes("403")) {
          if (isAuthenticated) {
            setError("You do not have permission to view this space");
          } else {
            setError("Please log in to view this space");
          }
        } else if (err.message?.includes("404")) {
          setError("Space not found");
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };
    if (spaceId) {
      fetchData();
    }
  }, [spacePageId, language, spaceId, isAuthenticated]);

  const isFavoritedFromRedux = useSelector(isFavorite(spaceData?.id));
  useEffect(() => {
    if (spaceData?.id) {
      setLocalIsFavorited(isFavoritedFromRedux);
    }
  }, [isFavoritedFromRedux, spaceData?.id]);

  useEffect(() => {
    if (!spaceData || !spaceData.address?.lat || !spaceData.address?.lng) return;
    const fetchNearbySpaces = async () => {
      try {
        const nearbySpacesData = await getNearbyPlaces({
          place_id: spaceId,
          lat: spaceData.address.lat,
          lng: spaceData.address.lng,
          lang: language,
          usage: [spaceData.placeUsage.key],
        });
        setNearbySpaces(nearbySpacesData || []);
      } catch (err) {
        console.error("Error fetching nearby spaces:", err);
        setNearbySpaces([]);
      }
    };
    fetchNearbySpaces();
  }, [spaceData, spaceId, language]);

  const handleBookingStart = () => {
    console.log('[SpacePage] Booking started');
    setIsBookingInProgress(true);
  };

  const handleBookingEnd = () => {
    console.log('[SpacePage] Booking ended');
    setIsBookingInProgress(false);
    // ============================================================
    // ADDED: Clear OAuth loading state when booking ends
    // ============================================================
    setIsOAuthLoading(false);
  };

  const handleFavoriteClick = async () => {
    if (isProcessingFavorite || !spaceData) return;
    setIsProcessingFavorite(true);
    const previousState = localIsFavorited;
    setLocalIsFavorited(!previousState);
    try {
      await dispatch(toggleAddToFavorite(spaceData, userType));
    } catch (error) {
      setLocalIsFavorited(previousState);
      console.error("Failed to toggle favorite:", error.message);
    } finally {
      setIsProcessingFavorite(false);
    }
  };

  const scrollToMap = () => {
    const mapElement = document.getElementById("location");
    if (mapElement) {
      const offset = 50;
      const elementPosition = mapElement.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  const scrollToBooking = () => {
    const element = document.getElementById("bookingSection");
    if (element) {
      const offset = 70;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  const handleMobileBookingClick = () => {
  if (!spaceData || spaceData.private) return;
    if (
      !isSeminarOrTeambuilding(spaceData.placeUsage?.key) &&
      spaceData.minimal_duration !== "month"
    ) {
      openModal({
        content: (
          <CompactBookingForm 
            space={spaceData}
            onBookingStart={handleBookingStart}
            onBookingEnd={handleBookingEnd}
            isModal={true}
          />
        ),
        title: t("space.askReservation"),
        type: "default",
        autoClose: false,
        size: "booking",
      });
    } else {
      openModal({
        content: (
          <RequestQuotationForm
            spaceId={spaceData.id}
            spaceType={spaceData.placeUsage?.key}
            spaceData={ spaceData}
          />
        ),
        title: t("modal.requestbooking.titleAsk"),
        type: "default",
        autoClose: false,
        size: "medium",
      });
    }
  };

  const handleDesktopBookingClick = () => {
    scrollToBooking();
  };

const handleBookingClick = () => {
  if (!spaceData || spaceData.private) return;
  if (isMobile) {
    handleMobileBookingClick();
  } else {
    handleDesktopBookingClick();
  }
};

  const getServicePrice = (service, currency, t) => {
    const isValidPrice = (price) => {
      return price !== null && price !== undefined && price !== "";
    };
    const formatPrice = (price) => {
      if (!isValidPrice(price)) return null;
      const numPrice = parseFloat(price);
      return isNaN(numPrice) ? null : numPrice;
    };
    if (!service.place_service?.location) {
      if (service.price === 0 || service.price === "0") {
        return t("space.services.onDemand");
      }
      const displayPrice = formatPrice(service.price);
      if (displayPrice !== null && displayPrice !== 0) {
        const unitServiceIds = [
          "d9298be9-3749-47f4-8ef7-42197b36fd3d",
          "93a76791-6209-4ed4-8b5a-7a1a15bd5e4e",
          "2bae8e81-2a85-49c9-8579-a3092ffcc61d",
          "d491b21b-dede-4b31-8017-2a2ea2ee8984",
          "b536732a-0acd-4d02-810f-2fbb81625fe8",
          "d5a363d6-a232-4dfb-a20d-4a69b3943e34",
        ];
        const unitTranslation = unitServiceIds.includes(service.place_service.id)
          ? t("space.services.byUnit")
          : `(${t("space.services.indicatedPrice")})`;
        return `${displayPrice} ${currencyToSymbol(currency)} ${unitTranslation}`;
      }
      return t("space.services.onDemand");
    }
    if (service.location_type === "included") {
      return t("space.services.included");
    }
    const formattedPrice = formatPrice(service.price);
    if (formattedPrice === null) {
      return t("space.services.onDemand");
    }
    const formattedLocationType = service.location_type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("");
    return `${formattedPrice} ${currencyToSymbol(currency)} ${t(
      `space.by${formattedLocationType}Short`,
    )}`;
  };

  const freeServicesRenderer = spaceData?.services
    ?.filter((service) => service.place_service?.type === "free" && service.available)
    .map((service) => (
      <div key={service.place_service.id} className={styles.serviceItem}>
        <ServiceSvgIcon iconPath={service.place_service.image_url} />
        <span className={styles.serviceTxt}>{service.place_service.name}</span>
      </div>
    )) || [];

  const reservationServicesRenderer = spaceData?.services
    ?.filter((service) => service.place_service?.type === "reservation" && service.available)
    .map((service) => (
      <div key={service.place_service.id} className={styles.serviceItem}>
        <ServiceSvgIcon iconPath={service.place_service.image_url} />
        <div className={styles.serviceItemText}>
          <span className={styles.serviceItemName}>{service.place_service.name}</span>
          <span className={styles.serviceItemPrice}>
            {getServicePrice(service, spaceData.currency, t)}
          </span>
          <span className={styles.serviceItemDescription}>{service.description}</span>
        </div>
      </div>
    )) || [];

  const onsiteServicesRenderer = spaceData?.services
    ?.filter(
      (service) =>
        service.place_service?.type === "onsite" &&
        service.available &&
        service.place_service.is_custom === 0
    )
    .map((service) => (
      <div key={service.place_service.id} className={styles.serviceItem}>
        <ServiceSvgIcon iconPath={service.place_service.image_url} />
        <div className={styles.serviceItemText}>
          <span className={styles.serviceItemName}>{service.place_service.name}</span>
          <span className={styles.serviceItemPrice}>
            {getServicePrice(service, spaceData.currency, t)}
          </span>
          <span className={styles.serviceItemDescription}>{service.description}</span>
        </div>
      </div>
    )) || [];

  const onsitePaymentServicesRenderer = spaceData?.services
    ?.filter(
      (service) =>
        (service.place_service?.type === "onsite" || service.place_service?.type === "") &&
        service.available &&
        service.place_service.is_custom === 1
    )
    .map((service) => (
      <div key={service.place_service.id} className={styles.serviceItem}>
        <ServiceSvgIcon iconPath={service.place_service.image_url} />
        <div className={styles.serviceItemText}>
          <span className={styles.serviceItemName}>{service.place_service.name}</span>
          <span className={styles.serviceItemPrice}>
            {getServicePrice(service, spaceData.currency, t)}
          </span>
          <span className={styles.serviceItemDescription}>{service.description}</span>
        </div>
      </div>
    )) || [];

  const capacityIconsRenderer = () => {
    if (!spaceData) return [];
    const filteredList = getFilteredDispositionTypes(spaceData, t);
    return filteredList.map((cap) => (
      <div className={styles.capacityItem} key={cap.key}>
        <Tooltip content={cap.name}>
          <cap.Icon className={styles.capacityItemImg} />
        </Tooltip>
        <span className={styles.capacityItemTxt}>
          {`${cap.count} ${t("provide.step2.area.people")}`}
        </span>
      </div>
    ));
  };

  const renderSeminars = () => {
    if (!spaceData || !Array.isArray(spaceData.seminars)) {
      return null;
    }
    const seminars = spaceData.seminars;
    const groupedSeminars = seminars
      .filter((seminar) => seminar.enable)
      .reduce((acc, seminar) => {
        const type = seminar.type;
        if (!acc[type]) acc[type] = [];
        acc[type].push(seminar);
        return acc;
      }, {});
    if (Object.keys(groupedSeminars).length === 0) {
      return <p>{t("space.noSeminarsAvailable")}</p>;
    }
    return Object.entries(groupedSeminars).map(([type, offers]) => {
      const typeTitle =
        type === "halfday"
          ? t("provide.step2.seminarService.halfdayTitle")
          : type === "day"
          ? t("provide.step2.seminarService.dayTitle")
          : type === "residential"
          ? t("provide.step2.seminarService.residentialTitle")
          : t("space.seminarUnknown");
      return (
        <div key={type} className={styles.seminarType}>
          <h4 className={styles.typeTitle}>{typeTitle}</h4>
          <ul className={styles.offerList}>
            {offers.map((offer) => (
              <li key={offer.id} className={styles.offerItem}>
                <div className={styles.offerDetails}>
                  <span className={styles.offerNumber}>
                    {`${t("space.seminar.offer")} n° ${offer.number}:`}
                  </span>
                  <p className={styles.offerDescription}>{offer.offer}</p>
                </div>
                <div className={styles.offerPrice}>
                  {offer.price &&
                    `${offer.price} ${currencyToSymbol(spaceData.currency)} ${t("space.seminar.price")}`}
                </div>
              </li>
            ))}
          </ul>
        </div>
      );
    });
  };

  const sections = spaceData
    ? [
        { id: "information", label: t("space.generalInformation") },
        ...(freeServicesRenderer?.length > 0
          ? [{ id: "characteristics", label: t("space.services.freeService") }]
          : []),
        ...(onsiteServicesRenderer?.length > 0
          ? [{ id: "onsiteServices", label: t("space.services.onSiteService") }]
          : []),
        ...(reservationServicesRenderer?.length > 0
          ? [{ id: "reservationServices", label: t("space.services.reservationService") }]
          : []),
        ...(onsitePaymentServicesRenderer?.length > 0
          ? [{ id: "onsiteServicesCustom", label: t("provide.step2.onSiteServiceCustom.title") }]
          : []),
        ...(spaceData?.seminars?.length > 0
          ? [{ id: "seminars", label: t("space.seminar.title") }]
          : []),
        { id: "location", label: t("space.geografic") },
        ...(spaceData?.note_avg ? [{ id: "reviews", label: t("space.notes") }] : []),
        ...(nearbySpaces.length > 0 ? [{ id: "nearby", label: t("space.nearbyPlaces") }] : []),
      ].filter(Boolean)
    : [];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 70;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (error || !spaceData) {
    if (spaceStatus === "rejected" && isAuthenticated) {
      return (
        <div className={styles.spaceErrorContainer}>
          <div className={styles.errorContent}>
            <h2>Space Rejected</h2>
            <p>This space has been rejected and is only visible to administrators.</p>
            <p className={styles.errorDetails}>Status: {spaceStatus}</p>
            <button className={styles.backButton} onClick={() => navigate(-1)}>
              Go Back
            </button>
          </div>
        </div>
      );
    }
    if (spaceStatus === "pending" && isAuthenticated) {
      return (
        <div className={styles.spaceErrorContainer}>
          <div className={styles.errorContent}>
            <h2>Space Pending Review</h2>
            <p>This space is currently under review and is only visible to administrators.</p>
            <p className={styles.errorDetails}>Status: {spaceStatus}</p>
            <button className={styles.backButton} onClick={() => navigate(-1)}>
              Go Back
            </button>
          </div>
        </div>
      );
    }
    return <NotFoundPage />;
  }

  // ============================================================
  // Show loading overlay during OAuth pending flows
  // ============================================================
const hasAuthToken = !!getToken();

  const isPendingFlow = 
    sessionStorage.getItem('just_logged_in_for_booking') === 'true' || 
    sessionStorage.getItem('just_registered_for_booking') === 'true' ||
    sessionStorage.getItem('has_pending_booking_after_oauth') === 'true';

  // Show overlay only if the user is actually authenticated.
  // An unauthenticated user cannot be mid-booking, so any "pending"
  // flags are stale and must not trigger the overlay.
  const showLoadingOverlay = 
    hasAuthToken && (
      (isBookingInProgress && isPendingFlow) || 
      (isOAuthLoading && isPendingFlow)
    );

  return (
    <>
      <SpacePageHelmet spaceData={spaceData} language={language} spaceStatus={spaceStatus} />
      {showLoadingOverlay && (
        <div className={styles.globalLoadingOverlay}>
          <div className={styles.globalLoadingContent}>
            <div className={styles.spinner}></div>
            <p>{t("booking.processing")}</p>
          </div>
        </div>
      )}
      <div className={`${styles.spacePageWrapper} pageContainer`}>
        {!embedded && (
          <div className={styles.pathsToSpace}>
            <a href={`/search?${getQueryParamsFromFilters(filters)}`}>{t("search.title")}</a>
            <span>/</span>
            {spaceData.address?.country && (
              <a href={`/search?country=${spaceData.address.country}`}>{spaceData.address.country}</a>
            )}
            <span>/</span>
            {spaceData.address?.postal_code && (
              <a href={`/search?postal_code=${spaceData.address.postal_code}`}>
                {spaceData.address.postal_code}
              </a>
            )}
            <span>/</span>
            {spaceData.address?.city && (
              <a href={`/search?city=${spaceData.address.city}`}>{spaceData.address.city}</a>
            )}
            <span>/</span>
            <span>{spaceData.name}</span>
          </div>
        )}
        <h1 className={styles.mainTitle}>{autogenerateSpaceTitle(spaceData, t)}</h1>
        <p className={styles.spaceId}>
          {t("space.space")} {spaceData.id}
        </p>
        <p className={styles.mapSectionAddress} onClick={scrollToMap}>
          <LocationIcon className={styles.locationIcon} />
          {`${getStreetName(spaceData?.address?.address)}, ${spaceData?.address?.postal_code}, ${spaceData?.address?.city}, ${spaceData?.address?.country}`}
        </p>
        <div className={styles.headerReviewsContainer}>
          {!!spaceData.note_avg && (
            <div className={styles.reviewStarsGlobal}>
              <Reviews spaceData={spaceData} t={t} />
            </div>
          )}
          <div className={styles.ctaBtnsContainer}>
            {userType !== "provider" && (
              <button
                className={`${styles.favoriteBtn} ${isProcessingFavorite ? styles.processing : ""}`}
                onClick={handleFavoriteClick}
                disabled={isProcessingFavorite}
                aria-label={localIsFavorited ? t("space.removeFromFavorite") : t("space.addToFavorite")}
              >
                <span
                  style={{
                    backgroundImage: `url(${localIsFavorited ? heartIconFilled : heartIconOutlined})`,
                  }}
                  className={styles.ctaAddToFavourite}
                  alt={localIsFavorited ? "remove from favourite" : "add to favourite"}
                />
              </button>
            )}
            <button
              className={styles.shareBtn}
              onClick={() =>
                openModal({
                  content: <ShareSpaceModal />,
                  title: t("modal.share.title"),
                })
              }
              aria-label={t("space.share")}
            >
              <span
                style={{
                  backgroundImage: `url(${ShareIcon})`,
                }}
                className={styles.ctaIcon}
              />
            </button>
            {userType !== "provider" && !spaceData.private && (
  <CTAButton
    variant="secondary"
    children={t("space.askReservation")}
    onClick={handleBookingClick}
    className={styles.reservationCtaBtn}
  />
)}
          </div>
        </div>
        <SpaceGallery images={spaceData.images?.map((image) => image.image_url) || []} language={language} />
        {sections.length > 0 && (
          <div className={styles.sectionNavigation} aria-label="Page sections">
            <div className={styles.sectionNavigationList}>
              {sections.map((section) => (
                <div key={section.id}>
                  <div
                    className={styles.sectionNavigationLink}
                    onClick={() => scrollToSection(section.id)}
                    aria-label={`Jump to ${section.label}`}
                  >
                    {section.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className={styles.spacePage}>
          <div className={styles.body}>
            <div className={styles.bodyContent}>
              <h2 className={styles.sectionTitle} id="information">
                {t("space.generalInformation")}
              </h2>
              {spaceData.placeUsage?.key === "coworking" ? (
                <div className={styles.spaceSizesSection}>
                  <h3 className={styles.subtitle}>{t("space.informationAboutPlace")} :</h3>
                  <div className={styles.spaceSizesIcons}>
                    <div className={styles.spaceSizeIconsRow}>
                      {spaceData.area !== 0 && (
                        <div className={styles.capacityItem}>
                          <Tooltip content={t("provide.step2.area.area")}>
                            <AreaIcon className={styles.capacityItemImg} />
                          </Tooltip>
                          <span className={styles.capacityItemTxt}>{spaceData?.area} m²</span>
                        </div>
                      )}
                      <div className={`${styles.capacityItem} ${styles.capacityItem_coworking}`}>
                        <span className={styles.capacityItemTitle}>
                          {t("provide.step2.area.numbercoworkingstationtotal")} :
                        </span>
                        <span className={styles.capacityItemTxt}>
                          {spaceData?.desk} {t("provide.step2.area.places")}
                        </span>
                      </div>
                      <div className={`${styles.capacityItem} ${styles.capacityItem_coworking}`}>
                        <span className={styles.capacityItemTitle}>
                          {t("provide.step2.area.workPlacesToBook")} :
                        </span>
                        <span className={styles.capacityItemTxt}>
                          {spaceData?.coworking_stations} {t("booking.step1.stations")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.spaceSizesSection}>
                  <p className={styles.subtitle}>{t("space.availableDispositions")}</p>
                  <div className={styles.spaceSizesIcons}>
                    {(capacityIconsRenderer().length || spaceData.area) && (
                      <div className={styles.spaceSizeIconsRow}>
                        {spaceData.area !== 0 && (
                          <div className={styles.capacityItem}>
                            <Tooltip content={t("provide.step2.area.area")}>
                              <AreaIcon className={styles.capacityItemImg} />
                            </Tooltip>
                            <span className={styles.capacityItemTxt}>{spaceData?.area} m²</span>
                          </div>
                        )}
                        {(spaceData.placeUsage?.key === "private_office" ||
                          spaceData.placeUsage?.key === "shared_office") && (
                          <div className={styles.capacityItem}>
                            <span className={styles.capacityItemTitle}>
                              {t("provide.step2.area.workPlaces")}:
                            </span>
                            <span className={styles.capacityItemTxt}>
                              {spaceData?.desk === 0 ? 1 : spaceData.desk} {t("provide.step2.area.places")}
                            </span>
                          </div>
                        )}
                        {capacityIconsRenderer()}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className={styles.descriptionSection}>
                <p className={styles.subtitle}>{spaceData.meta_title_h2 || spaceData.title}</p>
                <p dangerouslySetInnerHTML={{ __html: spaceData?.description || "" }} />
              </div>
              <>
                {freeServicesRenderer.length > 0 && (
                  <div className={styles.servicesSection} id="characteristics">
                    <h3 className={`${styles.subtitle} ${styles.subtitle_services}`}>
                      {t("space.services.freeService")}
                    </h3>
                    <div className={styles.servicesList}>{freeServicesRenderer}</div>
                  </div>
                )}
                {reservationServicesRenderer.length > 0 && (
                  <div className={styles.servicesSection} id="reservationServices">
                    <h3 className={`${styles.subtitle} ${styles.subtitle_services}`}>
                      {t("space.services.reservationService")}
                    </h3>
                    <div className={styles.servicesList}>{reservationServicesRenderer}</div>
                  </div>
                )}
                {onsiteServicesRenderer.length > 0 && (
                  <div className={styles.servicesSection} id="onsiteServices">
                    <h3 className={`${styles.subtitle} ${styles.subtitle_services}`}>
                      {t("space.services.onSiteService")}
                    </h3>
                    <div className={styles.servicesList}>{onsiteServicesRenderer}</div>
                  </div>
                )}
                {onsitePaymentServicesRenderer.length > 0 && (
                  <div className={styles.servicesSection}>
                    <h3 className={`${styles.subtitle} ${styles.subtitle_services}`}>
                      {t("provide.step2.onSiteServiceCustom.title")}
                    </h3>
                    <div className={styles.servicesList}>{onsitePaymentServicesRenderer}</div>
                  </div>
                )}
              </>
              {spaceData.seminars?.length > 0 && (
                <div className={styles.section} id="seminars">
                  <h3 className={`${styles.subtitle} ${styles.subtitle_services}`}>
                    {t("space.seminar.title")}
                  </h3>
                  <div className={styles.seminarSectionBody}>{renderSeminars()}</div>
                </div>
              )}
              <div className={`${styles.section} ${styles.mapSection}`} id="location">
                <h2 className={styles.sectionTitle}>{t("space.geografic")}</h2>
                <p className={styles.mapSectionAddress}>
                  <LocationIcon className={styles.locationIcon} />
                  {`${getStreetName(spaceData?.address?.address)}, ${spaceData?.address?.postal_code}, ${spaceData?.address?.city}, ${spaceData?.address?.country}`}
                </p>
                <SpaceLocationMap activeSpace={spaceData} setActiveSpace={setSpaceData} />
              </div>
              {!!spaceData.note_avg && (
                <div className={`${styles.section} ${styles.reviewsSection}`} id="reviews">
                  <h2 className={styles.sectionTitle}>{t("space.notes")}</h2>
                  <div className={`${styles.sectionBody} ${styles.reviewsSectionBody}`}>
                    <div className={styles.reviewStarsGlobal}>
                      <span className={styles.globalReviewText}>{t("space.global-rating")}</span>
                      <ReviewStars rating={spaceData.note_avg} />
                    </div>
                    <div className={styles.reviewStarsMore}>
                      <div className={styles.reviewStarsContainer}>
                        <ReviewStars title={t("space.ratingsQualityPrice")} rating={spaceData.quality_price_avg} />
                      </div>
                      <div className={styles.reviewStarsContainer}>
                        <ReviewStars title={t("space.ratingsLocation")} rating={spaceData.location_avg} />
                      </div>
                      <div className={styles.reviewStarsContainer}>
                        <ReviewStars title={t("space.ratingsServices")} rating={spaceData.quality_services_avg} />
                      </div>
                      <div className={styles.reviewStarsContainer}>
                        <ReviewStars title={t("space.ratingsEquipments")} rating={spaceData.equipments_avg} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {nearbySpaces.length > 0 && !embedded && (
                <div className={`${styles.section} ${styles.nearbyPlacesSection}`} id="nearby">
                  <h2 className={styles.sectionTitle}>{t("space.nearbyPlaces")}</h2>
                  <LandingPageSlider spaces={nearbySpaces} largeBreakpoint={1450} />
                </div>
              )}
            </div>
            {/* Desktop Booking Section */}
<div className={styles.bookingSection} id="bookingSection">
  {spaceData.private ? (
    <div className={styles.privateSpaceNotice}>
      <h3 className={styles.privateSpaceNoticeTitle}>
        {t("space.privateSpaceNotice.title")}
      </h3>
      <p className={styles.privateSpaceNoticeMessage}>
        {t("space.privateSpaceNotice.message")}
      </p>
    </div>
  ) : (
    <>
      <div className={styles.bookingFormContainer}>
        {!isSeminarOrTeambuilding(spaceData.placeUsage?.key) &&
        spaceData.minimal_duration !== "month" ? (
          <CompactBookingForm 
            space={spaceData}
            onBookingStart={handleBookingStart}
            onBookingEnd={handleBookingEnd}
            isModal={false}
          />
        ) : (
          <PricesTable space={spaceData} />
        )}
        {(!isSeminarOrTeambuilding(spaceData.placeUsage?.key) &&
          spaceData.minimal_duration !== "month") ? null : (
          <div
            className={styles.requestQuotationBtn}
            onClick={() =>
              openModal({
                content: (
                  <RequestQuotationForm
                    spaceId={spaceData.id}
                    spaceType={spaceData.placeUsage?.key}
                    spaceData={spaceData}
                  />
                ),
                title: !isSeminarOrTeambuilding(spaceData.placeUsage?.key) &&
                  spaceData.minimal_duration !== "month"
                  ? t("modal.requestbooking.titleAsk")
                  : t("modal.requestbooking.title"),
                type: "default",
                autoClose: false,
                size: "medium",
              })
            }
          >
            {isSeminarOrTeambuilding(spaceData.placeUsage?.key)
              ? t("space.askReservationSeminar")
              : t("space.askQuestion")}
            <AskAQuestionIcon />
          </div>
        )}
      </div>
      {spaceData.lockAvailable && (
        <button
          className={`${styles.lockManagementBtn}`}
          onClick={() =>
            openModal({
              content: <SimpleConfirmationModal text={t("space.accessibleByCodeText")} />,
              title: t("space.accessibleByCodeTitle"),
              size: "small",
            })
          }
        >
          {t("space.lockAvailable")}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 2C9.23858 2 7 4.23858 7 7V9H6C4.89543 9 4 9.89543 4 11V19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 20.1046 20 19V11C20 9.89543 19.1046 9 18 9H17V7C17 4.23858 14.7614 2 12 2ZM9 7C9 5.34315 10.3431 4 12 4C13.6569 4 15 5.34315 15 7V9H9V7Z"
              fill="white"
            />
            <path
              d="M12 14C11.4477 14 11 14.4477 11 15V17C11 17.5523 11.4477 18 12 18C12.5523 18 13 17.5523 13 17V15C13 14.4477 12.5523 14 12 14Z"
              fill="white"
            />
          </svg>
        </button>
      )}
    </>
  )}
</div>
{/* Mobile Booking Section */}
<div className={styles.bookingSectionMobile}>
  {spaceData.private ? (
    <div className={styles.privateSpaceNotice}>
      <h3 className={styles.privateSpaceNoticeTitle}>
        {t("space.privateSpaceNotice.title")}
      </h3>
      <p className={styles.privateSpaceNoticeMessage}>
        {t("space.privateSpaceNotice.message")}
      </p>
    </div>
  ) : (
    <>
      <PricesTable space={spaceData} />
      {!isSeminarOrTeambuilding(spaceData.placeUsage?.key) &&
      spaceData.minimal_duration !== "month" ? (
        <div
          className={styles.requestBookingBtn}
          onClick={handleMobileBookingClick}
        >
          {t("space.askReservation")}
        </div>
      ) : (
        <div
          className={styles.requestQuotationBtn}
          onClick={() =>
            openModal({
              content: (
                <RequestQuotationForm
                  spaceId={spaceData.id}
                  spaceType={spaceData.placeUsage?.key}
                  spaceData={spaceData}
                />
              ),
              title: t("space.askQuestion"),
              type: "default",
              autoClose: false,
              size: "medium",
            })
          }
        >
          {isSeminarOrTeambuilding(spaceData.placeUsage?.key)
            ? t("space.askReservationSeminar")
            : t("space.askQuestion")}
        </div>
      )}
      {spaceData.lockAvailable && (
        <button
          className={`${styles.lockManagementBtn}`}
          onClick={() =>
            openModal({
              content: <SimpleConfirmationModal text={t("space.accessibleByCodeText")} />,
              title: t("space.accessibleByCodeTitle"),
              size: "small",
            })
          }
        >
          {t("space.lockAvailable")}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 2C9.23858 2 7 4.23858 7 7V9H6C4.89543 9 4 9.89543 4 11V19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 20.1046 20 19V11C20 9.89543 19.1046 9 18 9H17V7C17 4.23858 14.7614 2 12 2ZM9 7C9 5.34315 10.3431 4 12 4C13.6569 4 15 5.34315 15 7V9H9V7Z"
              fill="white"
            />
            <path
              d="M12 14C11.4477 14 11 14.4477 11 15V17C11 17.5523 11.4477 18 12 18C12.5523 18 13 17.5523 13 17V15C13 14.4477 12.5523 14 12 14Z"
              fill="white"
            />
          </svg>
        </button>
      )}
    </>
  )}
</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SpacePage;

